import React, { useState } from 'react';
import { Button } from '@vaiu/ui/button';
import { ManualRedemptionUI } from '@vaiu/redeem-ux';
import { useToast } from '@/hooks/use-toast';
import { i18n } from '@/utils/i18n';
import type { InboxTransaction } from '@/utils/inboxFilters';

interface DepositInboxActionProps {
  token: InboxTransaction;
  onDeposited?: () => void;
}

function resolveDepositTokenKey(token: InboxTransaction): string | undefined {
  const candidates = [token.key, token.tokenKey]
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter(Boolean);
  const sixChar = candidates.find((key) => key.length === 6);
  return sixChar || candidates[0] || undefined;
}

function resolveFee(token: InboxTransaction): number {
  const payload =
    token.payload && typeof token.payload === 'object'
      ? (token.payload as Record<string, unknown>)
      : {};
  const data =
    token.data && typeof token.data === 'object' ? (token.data as Record<string, unknown>) : {};
  const nestedPayload =
    data.payload && typeof data.payload === 'object'
      ? (data.payload as Record<string, unknown>)
      : {};
  const raw =
    (token as { transactionFee?: unknown }).transactionFee ??
    (token as { fee?: unknown }).fee ??
    payload.transactionFee ??
    payload.fee ??
    nestedPayload.transactionFee ??
    nestedPayload.fee ??
    0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function resolveNetAmount(token: InboxTransaction, amount: number, fee: number): number {
  const payload =
    token.payload && typeof token.payload === 'object'
      ? (token.payload as Record<string, unknown>)
      : {};
  const raw =
    (token as { netAmount?: unknown }).netAmount ??
    payload.netAmount;
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 0) return n;
  return Math.max(0, amount - fee);
}

export const DepositInboxAction: React.FC<DepositInboxActionProps> = ({
  token,
  onDeposited,
}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const tokenKey = resolveDepositTokenKey(token);
  const amount = Number(token.amount) || 0;
  const currency = token.currency || 'USD';
  const fee = resolveFee(token);
  const netAmount = resolveNetAmount(token, amount, fee);

  if (!tokenKey) {
    return (
      <div className="px-4 pb-3">
        <Button
          type="button"
          className="w-full"
          variant="destructive"
          onClick={(e) => {
            e.stopPropagation();
            toast({
              title: i18n.__('inbox.deposit.failedTitle'),
              description: i18n.__('inbox.deposit.missingKey'),
              variant: 'destructive',
            });
          }}
        >
          {i18n.__('inbox.deposit.button')}
        </Button>
      </div>
    );
  }

  if (open) {
    return (
      <div
        className="px-4 pb-3"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <ManualRedemptionUI
          tokenKey={tokenKey}
          tokenType="cash"
          amount={amount}
          currency={currency}
          message={typeof token.message === 'string' ? token.message : undefined}
          transactionFee={fee}
          netAmount={netAmount}
          onComplete={() => {
            setOpen(false);
            onDeposited?.();
          }}
          onCancel={() => setOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="px-4 pb-3">
      <Button
        type="button"
        className="w-full"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {i18n.__('inbox.deposit.button')}
      </Button>
    </div>
  );
};

export default DepositInboxAction;
